import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FlexBox, Typography } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import CurrencyWrapper from '../../shared/pricing/CurrencyWrapper';
import useSiteData from '../../../hooks/useSiteData';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { CREDIT_CARD, CREDITS } from '../../../constants/paymentMethod';
import { PaymentMethodLogo } from './PaymentMethodLogo';

export const OrderPaymentMethod = ({ paymentDetail }) => {
  const { order } = useContext(OrderDetailsContext);
  const { locale } = useSiteData();
  const [paymentMessages] = useTranslations(['paymentMessages']);
  const { paymentMethod, paymentMethodType, label } = paymentDetail;
  // If payment method is credit card, we show translation for "ending in..." to customers
  const isCreditCard = paymentMethodType === CREDIT_CARD;
  // Is merch credit
  const isMerchCredit = paymentMethod === CREDITS;

  // Typography and om-translation don't work well so we have to make a string first
  const getLabelDisplayString = () => {
    if (!label) return '';

    return isCreditCard ? `**** ${label}` : label;
  };

  const paymentMethodTitle = () =>
    isMerchCredit ? paymentMessages.merchCredits : paymentMethod;

  const amount = paymentDetail.amount?.value;
  const currency = paymentDetail.amount?.currency;

  const validForDisplay = amount !== 0 && currency;

  return (
    <FlexBox flexDirection="column">
      <FlexBox alignItems="center" justifyContent="space-between">
        <FlexBox flexWrap="wrap" alignItems="center">
          <PaymentMethodLogo
            paymentMethod={paymentMethod}
            paymentMethodType={paymentMethodType}
          />
          <Typography
            mr={3}
            fontWeight="bold"
            data-testid="payment-method-title"
          >
            {`${paymentMethodTitle(paymentMethod)}`}
          </Typography>
        </FlexBox>
        {validForDisplay && (
          <Typography data-testid="payment-details-amount">
            <CurrencyWrapper
              fractionDigits={order.fractionalDigits}
              typeSize={2}
              listCents={amount}
              discountCents={amount}
              currencyCode={currency}
              locale={locale}
            />
          </Typography>
        )}
      </FlexBox>
      <FlexBox>
        {!!label && (
          <Typography data-testid="payment-details-label">
            {getLabelDisplayString()}
          </Typography>
        )}
      </FlexBox>
    </FlexBox>
  );
};

OrderPaymentMethod.propTypes = {
  paymentDetail: PropTypes.object.isRequired,
};
